import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useAuthState } from 'react-firebase-hooks/auth';
import moment from 'moment-timezone';
import FirebaseAuth from '../services/FirebaseAuth';
import Loader from '../components/Loader';
import './TelegramRoute.css';

// Telegram WebApp JS bridge injected by the client.
declare const Telegram: {
  WebApp: {
    initData: string;
    close: () => void;
  };
} | undefined;

function isTelegramContext(): boolean {
  try {
    return typeof Telegram !== 'undefined' && !!Telegram.WebApp.initData;
  } catch {
    return false;
  }
}

interface TelegramSession {
  chatId: string;
  firstName: string;
  username?: string;
  email: string;
  linkedAt: number;
  country?: string;
}

interface SnackMsg {
  type: 'success' | 'error';
  message: string;
  isShown: boolean;
}

// ─── Telegram Mini App linking view ──────────────────────────────────────────
// When opened inside Telegram we skip the sessions page and immediately link
// the Firebase account to the current chat by POSTing to the TG service /link
// endpoint. sendData() is NOT used because it only works for Reply keyboard
// buttons, not Inline keyboard webApp buttons.

const TG_SERVICE_URL = 'https://tg.miniaccountant.app';

function TelegramLinkView() {
  const [user] = useAuthState(FirebaseAuth);
  const [status, setStatus] = useState<'idle' | 'linking' | 'done' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!user || status !== 'idle') return;
    setStatus('linking');

    (async () => {
      try {
        const idToken = await user.getIdToken();
        const initData = (typeof Telegram !== 'undefined' ? Telegram.WebApp.initData : '') ?? '';

        const resp = await fetch(`${TG_SERVICE_URL}/link`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idToken, initData }),
        });

        if (!resp.ok) {
          const data = (await resp.json().catch(() => ({}))) as { message?: string };
          throw new Error(data.message ?? `HTTP ${resp.status}`);
        }

        setStatus('done');
        // Close the Mini App window — Telegram will return the user to the bot chat.
        if (typeof Telegram !== 'undefined') {
          Telegram.WebApp.close();
        }
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : 'Unknown error');
        setStatus('error');
      }
    })();
  }, [user, status]);

  if (!user) {
    return (
      <div className="TelegramRoute TelegramRoute--loading">
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Signing in…</Typography>
      </div>
    );
  }

  if (status === 'linking' || status === 'idle') {
    return (
      <div className="TelegramRoute TelegramRoute--loading">
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Linking account…</Typography>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="TelegramRoute TelegramRoute--loading">
        <Alert severity="error">{errorMsg}</Alert>
      </div>
    );
  }

  return (
    <div className="TelegramRoute TelegramRoute--loading">
      <Alert severity="success">Account linked! You can close this window.</Alert>
    </div>
  );
}

// ─── Sessions management view (browser only) ─────────────────────────────────

function TelegramSessionsView() {
  // When opened inside the Telegram client, use the linking flow instead.

  const [user] = useAuthState(FirebaseAuth);
  const [sessions, setSessions] = useState<TelegramSession[] | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmChatId, setConfirmChatId] = useState<string | null>(null);
  const [showMsg, setShowMsg] = useState<SnackMsg>({
    type: 'success',
    message: '',
    isShown: false,
  });

  const fetchSessions = useCallback(
    async (signal?: AbortSignal) => {
      if (!user) return;
      try {
        const token = await user.getIdToken();
        const res = await fetch('/v1/telegram/sessions', {
          headers: { Authorization: `Bearer ${token}` },
          signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const ct = res.headers.get('content-type') ?? '';
        if (!ct.includes('application/json')) {
          throw new Error(
            'Telegram sessions API is unavailable in this environment.'
          );
        }
        let data: { sessions: TelegramSession[] };
        try {
          data = (await res.json()) as { sessions: TelegramSession[] };
        } catch {
          throw new Error(
            'Telegram sessions API returned a non-JSON response.'
          );
        }
        if (!signal?.aborted) setSessions(data.sessions);
      } catch (err: unknown) {
        if (signal?.aborted) return;
        const msg =
          err instanceof Error ? err.message : 'Failed to load sessions';
        setShowMsg({ type: 'error', message: msg, isShown: true });
        setSessions([]);
      }
    },
    [user]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchSessions(controller.signal);
    return () => controller.abort();
  }, [fetchSessions]);

  const handleDelete = async (chatId: string) => {
    if (!user) return;
    setConfirmChatId(null);
    setDeleting(chatId);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`/v1/telegram/sessions/${chatId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setShowMsg({
        type: 'success',
        message: 'Session unlinked',
        isShown: true,
      });
      setSessions((prev) => prev?.filter((s) => s.chatId !== chatId) ?? null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to delete session';
      setShowMsg({ type: 'error', message: msg, isShown: true });
    } finally {
      setDeleting(null);
    }
  };

  if (sessions === null) {
    return <Loader />;
  }

  return (
    <div className="TelegramRoute">
      <Alert severity="info" sx={{ marginBottom: 2 }}>
        <AlertTitle>Telegram bot</AlertTitle>
        <Typography variant="body2">
          Start a chat with{' '}
          <Link
            href="https://t.me/ai_mini_accountant_bot"
            target="_blank"
            rel="noopener noreferrer"
          >
            @ai_mini_accountant_bot
          </Link>{' '}
          and tap the linking button to connect your Telegram account. Linked
          chats appear in the table below.
        </Typography>
      </Alert>
      {sessions.length === 0 ? (
        <Toolbar sx={{ justifyContent: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No Telegram accounts linked yet.
          </Typography>
        </Toolbar>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} size="small" aria-label="telegram sessions">
            <TableHead>
              <TableRow>
                <TableCell align="center">Chat ID</TableCell>
                <TableCell align="center">Name</TableCell>
                <TableCell align="center">Username</TableCell>
                <TableCell align="center">Email</TableCell>
                <TableCell align="center">Country</TableCell>
                <TableCell align="center">Linked At</TableCell>
                <TableCell align="center">Unlink</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((s) => (
                <TableRow
                  key={`tg_row_${s.chatId}`}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell align="center">{s.chatId}</TableCell>
                  <TableCell align="center">{s.firstName}</TableCell>
                  <TableCell align="center">{s.username ? `@${s.username}` : '—'}</TableCell>
                  <TableCell align="center">{s.email}</TableCell>
                  <TableCell align="center">{s.country ?? '—'}</TableCell>
                  <TableCell align="center">
                    {moment(s.linkedAt).format('YYYY-MM-DD HH:mm')}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      disabled={deleting === s.chatId}
                      onClick={() => setConfirmChatId(s.chatId)}
                    >
                      {deleting === s.chatId ? (
                        <CircularProgress size={18} />
                      ) : (
                        <DeleteOutlineOutlinedIcon />
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={showMsg.isShown}
        autoHideDuration={6000}
        onClose={() => setShowMsg({ ...showMsg, isShown: false })}
        sx={{ width: '100%', padding: '3vmin', justifyContent: 'end' }}
      >
        <Alert
          onClose={() => setShowMsg({ ...showMsg, isShown: false })}
          sx={{ marginRight: '3vmin' }}
          severity={showMsg.type}
        >
          {showMsg.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={confirmChatId !== null}
        onClose={() => setConfirmChatId(null)}
      >
        <DialogTitle>Unlink Telegram session?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will disconnect the Telegram chat from your account. The user
            will need to link again via the bot to regain access.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmChatId(null)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => confirmChatId && handleDelete(confirmChatId)}
          >
            Unlink
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// ─── Route entry point ────────────────────────────────────────────────────────

function TelegramRoute() {
  return isTelegramContext() ? <TelegramLinkView /> : <TelegramSessionsView />;
}

export default TelegramRoute;
